import * as THREE from "three";
import { Cell } from "@highmarch/domain";
import type { Terrain } from "./terrain";

/**
 * World y of the highest solid thing under a world point, looking down from `fromY`: the drawn ground sheet (`sheet`,
 * NaN over a hole — and above `fromY` too: a point under the sheet is buried), whatever of `meshes` (a gorge's walls and floor) a downward ray meets first, or `floor` — where a
 * bridge post comes to rest (bridgeFit.ts). GroundMesh.solidBelow asks it of the ground in the scene.
 */
export function probeDown(meshes: readonly THREE.Object3D[], sheet: number, floor: number, x: number, z: number, fromY: number): number {
  // a point under the drawn sheet is inside the ground: the sheet is what it stands in
  let best = Number.isFinite(sheet) ? Math.max(floor, sheet) : floor;
  if (best >= fromY) return best;
  if (meshes.length === 0) return best;
  const top = Math.min(fromY, best + ProbeHeight);
  origin.set(x, top, z);
  ray.set(origin, down);
  ray.far = top - best + ProbeHeight;
  hits.length = 0;
  ray.intersectObjects(meshes as THREE.Object3D[], false, hits);
  return hits.length > 0 ? Math.max(best, hits[0].point.y) : best;
}

/** Highest a probe starts above the floor under it, in world units (well over any gorge's depth). */
const ProbeHeight = 6;
const ray = new THREE.Raycaster();
const origin = new THREE.Vector3();
const down = new THREE.Vector3(0, -1, 0);
const hits: THREE.Intersection[] = [];

/**
 * The bottom under a point of the level's holes: a chasm's floor, or in a lava pool `LavaSink` under the melt — the
 * molten surface is opaque, and a bridge leg or post may stand in it (the basin is only ~1 unit deep).
 */
export function floorUnder(t: Terrain, x: number, z: number): number {
  return t.isLava(new Cell(Math.round(x), Math.round(z))) ? t.lavaAt(x, z) - LavaSink : t.floorAt(x, z);
}
export const LavaSink = 0.5;

/**
 * The triangles of `mesh` that fall within the box [x0, x1] × [z0, z1] (world xz), as a mesh of their own: a bridge
 * fit probes the gorge walls a few hundred times in one small area, and a ray against a few hundred triangles instead
 * of the whole map's thousands keeps laying a bridge card from costing a visible hitch.
 */
export function meshWithin(mesh: THREE.Mesh, x0: number, z0: number, x1: number, z1: number): THREE.Mesh {
  const pos = mesh.geometry.getAttribute("position"), idx = mesh.geometry.getIndex();
  const out: number[] = [];
  const n = idx ? idx.count : pos.count;
  for (let t = 0; t + 2 < n; t += 3) {
    let inside = false;
    for (let k = 0; k < 3 && !inside; k++) {
      const v = idx ? idx.getX(t + k) : t + k;
      const x = pos.getX(v), z = pos.getZ(v);
      inside = x >= x0 && x <= x1 && z >= z0 && z <= z1;
    }
    if (!inside) continue;
    for (let k = 0; k < 3; k++) { const v = idx ? idx.getX(t + k) : t + k; out.push(pos.getX(v), pos.getY(v), pos.getZ(v)); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(out, 3));
  return new THREE.Mesh(g, probeMaterial);
}
/** Rays hit both faces of a probe mesh (the walls are drawn double-sided). */
const probeMaterial = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide });
